import { useState, useMemo, useEffect, useRef } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import { Container, Card, PageContainer, PageHeader, Badge, InfoCard, BackButton } from '../../../styles/global'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { StructureCanvas } from './StructureCanvas'
import {
  DEFAULT_NODES,
  DEFAULT_ELEMENTS,
  DEFAULT_SUPPORTS,
  DEFAULT_NODAL_LOADS,
  DEFAULT_ELEMENT_LOADS,
  DEFAULT_MATERIALS,
  DEFAULT_SECTIONS,
  DEFAULT_LOAD_CASES,
  DEFAULT_COMBINATIONS,
  mapNodesForApi,
  mapElementsForApi,
  mapSupportsForApi,
  mapCombinationOverridesForApi,
  createSection,
  computeSectionProperties,
} from './model'
import {
  DEFAULT_PATH,
  DEFAULT_TRAINS,
  MOVING_RESPONSES,
  mapPathForApi,
  mapTrainsForApi,
  validateMovingLoads,
} from './movingLoadsModel'

export function ConcretePortico2D() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [nodes, setNodes] = useState(DEFAULT_NODES)
  const [elements, setElements] = useState(DEFAULT_ELEMENTS)
  const [supports, setSupports] = useState(DEFAULT_SUPPORTS)
  const [nodalLoads, setNodalLoads] = useState(DEFAULT_NODAL_LOADS)
  const [elementLoads, setElementLoads] = useState(DEFAULT_ELEMENT_LOADS)
  const [materials, setMaterials] = useState(DEFAULT_MATERIALS)
  const [sections, setSections] = useState(DEFAULT_SECTIONS)
  const [loadCases, setLoadCases] = useState(DEFAULT_LOAD_CASES)
  const [combinations, setCombinations] = useState(DEFAULT_COMBINATIONS)

  const [result, setResult] = useState('')
  const [data, setData] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const [movingLoadsPath, setMovingLoadsPath] = useState(DEFAULT_PATH)
  const [trains, setTrains] = useState(DEFAULT_TRAINS)
  const [movingLoadsResult, setMovingLoadsResult] = useState('')
  const [movingLoadsData, setMovingLoadsData] = useState({})

  const nodeIds = useMemo(() => nodes.map((n) => n.id).filter((id) => id !== ''), [nodes])
  const elementIds = useMemo(() => elements.map((e) => e.id).filter((id) => id !== ''), [elements])
  const materialIds = useMemo(() => materials.map((m) => m.id).filter((id) => id !== ''), [materials])
  const sectionIds = useMemo(() => sections.map((s) => s.id).filter((id) => id !== ''), [sections])
  const loadCaseIds = useMemo(() => loadCases.map((c) => c.id).filter((id) => id !== ''), [loadCases])
  const trainIds = useMemo(() => trains.map((t) => t.id).filter((id) => id !== ''), [trains])

  const formErrors = useMemo(() => {
    const errors = []

    if (nodes.length === 0) {
      errors.push('Adicione ao menos um nó')
    }
    if (elements.length === 0) {
      errors.push('Adicione ao menos um elemento')
    }
    if (loadCases.length === 0) {
      errors.push('Adicione ao menos um caso de carga')
    }

    const seenCaseIds = new Set()
    loadCases.forEach((c, index) => {
      if (c.id === '' || c.id === null || c.id === undefined) {
        errors.push(`Caso de carga #${index + 1}: id obrigatório`)
      } else if (seenCaseIds.has(c.id)) {
        errors.push(`Caso de carga "${c.id}" duplicado`)
      } else {
        seenCaseIds.add(c.id)
      }
    })

    const seenCombinationIds = new Set()
    combinations.forEach((c, index) => {
      const label = c.id || `#${index + 1}`
      if (c.id === '' || c.id === null || c.id === undefined) {
        errors.push(`Combinação #${index + 1}: id obrigatório`)
      } else if (seenCombinationIds.has(c.id)) {
        errors.push(`Combinação "${c.id}" duplicada`)
      } else {
        seenCombinationIds.add(c.id)
      }
      const factorCaseIds = Object.keys(c.factors || {})
      const factorTrainIds = Object.keys(c.trainFactors || {})
      if (factorCaseIds.length === 0 && factorTrainIds.length === 0) {
        errors.push(`Combinação "${label}": adicione ao menos um fator`)
      }
      factorCaseIds.forEach((caseId) => {
        if (!loadCaseIds.includes(caseId)) errors.push(`Combinação "${label}": caso "${caseId}" inválido`)
      })
    })

    combinations.forEach((c) => {
      const label = c.id || ''
      const factorTrainIds = Object.keys(c.trainFactors || {})
      factorTrainIds.forEach((trainId) => {
        if (!trainIds.includes(trainId)) {
          errors.push(`Combinacao "${label}": trem "${trainId}" invalido`)
          return
        }
      })
    })

    combinations.forEach((c) => {
      const label = c.id || ''
      Object.entries(c.elementOverrides || {}).forEach(([elementId, sectionId]) => {
        if (!elementIds.includes(elementId)) {
          errors.push(`Combinação "${label}": override refere-se a elemento "${elementId}" inválido`)
        }
        if (sectionId && !sectionIds.includes(sectionId)) {
          errors.push(`Combinação "${label}": override do elemento "${elementId}" usa seção "${sectionId}" inválida`)
        }
      })
    })

    materials.forEach((m, index) => {
      if (m.E === '' || isNaN(Number(m.E)) || Number(m.E) <= 0) {
        errors.push(`Material "${m.id || index + 1}": E inválido`)
      }
      if (m.specificWeight !== '' && m.specificWeight !== undefined && m.specificWeight !== null) {
        if (isNaN(Number(m.specificWeight)) || Number(m.specificWeight) < 0) {
          errors.push(`Material "${m.id || index + 1}": peso específico inválido`)
        }
      }
    })

    sections.forEach((s, index) => {
      const label = s.id || `#${index + 1}`
      if (!materialIds.includes(s.materialId)) errors.push(`Seção "${label}": material inválido`)
      const positive = (value, fieldLabel) => {
        if (value === '' || isNaN(Number(value)) || Number(value) <= 0) {
          errors.push(`Seção "${label}": ${fieldLabel} inválida`)
        }
      }
      if (s.shape === 'rectangular') {
        positive(s.b, 'base (b)')
        positive(s.h, 'altura (h)')
      } else if (s.shape === 'tSection' || s.shape === 'invertedTSection' || s.shape === 'iSection') {
        positive(s.bf, 'largura da mesa (bf)')
        positive(s.hf, s.shape === 'iSection' ? 'espessura da mesa (tf)' : 'altura da mesa (hf)')
        positive(s.bw, s.shape === 'iSection' ? 'espessura da alma (tw)' : 'largura da alma (bw)')
        positive(s.h, 'altura total (h)')
      } else if (s.shape === 'generic') {
        positive(s.customA, 'área (A)')
        if (s.customI === '' || isNaN(Number(s.customI)) || Number(s.customI) < 0) {
          errors.push(`Seção "${label}": momento de inércia (I) inválido`)
        }
      }
    })

    const seenNodeIds = new Set()
    nodes.forEach((n, index) => {
      if (n.id === '' || n.id === null || n.id === undefined) {
        errors.push(`Nó #${index + 1}: id obrigatório`)
      } else if (seenNodeIds.has(n.id)) {
        errors.push(`Nó "${n.id}" duplicado`)
      } else {
        seenNodeIds.add(n.id)
      }
      if (n.x === '' || isNaN(Number(n.x))) {
        errors.push(`Nó "${n.id || index + 1}": coordenada x inválida`)
      }
      if (n.y === '' || isNaN(Number(n.y))) {
        errors.push(`Nó "${n.id || index + 1}": coordenada y inválida`)
      }
    })

    elements.forEach((el, index) => {
      const label = el.id || `#${index + 1}`
      if (el.id === '') errors.push(`Elemento #${index + 1}: id obrigatório`)
      if (!nodeIds.includes(el.nodeI)) errors.push(`Elemento "${label}": nó inicial inválido`)
      if (!nodeIds.includes(el.nodeJ)) errors.push(`Elemento "${label}": nó final inválido`)
      if (el.nodeI && el.nodeI === el.nodeJ) errors.push(`Elemento "${label}": nó inicial e final iguais`)
      if (!sectionIds.includes(el.sectionId)) errors.push(`Elemento "${label}": seção inválida`)
    })

    const hasSelfWeightCase = loadCases.some((c) => c.selfWeight)
    if (hasSelfWeightCase) {
      elements.forEach((el, index) => {
        const label = el.id || `#${index + 1}`
        const section = sections.find((s) => s.id === el.sectionId)
        const material = materials.find((m) => m.id === section?.materialId)
        const specificWeight = material?.specificWeight
        if (
          specificWeight === '' ||
          specificWeight === undefined ||
          specificWeight === null ||
          isNaN(Number(specificWeight))
        ) {
          errors.push(`Elemento "${label}": peso específico do material é obrigatório para peso próprio`)
        }
      })
    }

    supports.forEach((s, index) => {
      if (!nodeIds.includes(s.node)) errors.push(`Apoio #${index + 1}: nó inválido`)
    })

    nodalLoads.forEach((l, index) => {
      if (!nodeIds.includes(l.node)) errors.push(`Carga nodal #${index + 1}: nó inválido`)
      if (!loadCaseIds.includes(l.loadCase)) errors.push(`Carga nodal #${index + 1}: caso de carga inválido`)
    })

    elementLoads.forEach((l, index) => {
      if (!elementIds.includes(l.element)) errors.push(`Carga em elemento #${index + 1}: elemento inválido`)
      if (!loadCaseIds.includes(l.loadCase)) errors.push(`Carga em elemento #${index + 1}: caso de carga inválido`)
      if (l.type === 'point' && (l.position === '' || isNaN(Number(l.position)))) {
        errors.push(`Carga em elemento #${index + 1}: posição inválida`)
      }
    })

    return errors
  }, [
    nodes,
    elements,
    supports,
    nodalLoads,
    elementLoads,
    materials,
    sections,
    loadCases,
    combinations,
    nodeIds,
    elementIds,
    materialIds,
    sectionIds,
    loadCaseIds,
    trainIds,
  ])

  const movingLoadsFormErrors = useMemo(
    () => validateMovingLoads({ path: movingLoadsPath, trains, elementIds }),
    [movingLoadsPath, trains, elementIds]
  )

  // The main "Calcular" button also runs the moving-loads request when a train is configured, so
  // an invalid moving-loads config (path/train) blocks it too, not just the static-model errors.
  const combinedFormErrors = trains.length > 0 ? [...formErrors, ...movingLoadsFormErrors] : formErrors
  const isFormValid = combinedFormErrors.length === 0

  function buildLoadCasesPayload() {
    return loadCases.map((c) => ({
      id: c.id,
      name: c.name || undefined,
      category: c.category || undefined,
      selfWeight: c.selfWeight || undefined,
      nodalLoads: nodalLoads
        .filter((l) => l.loadCase === c.id)
        .map((l) => ({
          node: l.node,
          Fx: Number(l.Fx) || 0,
          Fy: Number(l.Fy) || 0,
          Mz: Number(l.Mz) || 0,
        })),
      elementLoads: elementLoads
        .filter((l) => l.loadCase === c.id)
        .map((l) => {
          if (l.type === 'point') {
            return {
              element: l.element,
              type: 'point',
              coordinateSystem: l.coordinateSystem,
              position: Number(l.position) || 0,
              Px: Number(l.Px) || 0,
              Py: Number(l.Py) || 0,
              Mz: Number(l.Mz) || 0,
            }
          }
          return {
            element: l.element,
            type: 'distributed',
            coordinateSystem: l.coordinateSystem,
            qxI: Number(l.qxI) || 0,
            qxJ: l.qxJ === '' || l.qxJ === undefined ? Number(l.qxI) || 0 : Number(l.qxJ),
            qyI: Number(l.qyI) || 0,
            qyJ: l.qyJ === '' || l.qyJ === undefined ? Number(l.qyI) || 0 : Number(l.qyJ),
          }
        }),
    }))
  }

  function buildCombinationsPayload() {
    return combinations
      .filter(
        (c) =>
          !Object.values(c.trainFactors || {}).some((factor) => {
            const n = Number(factor)
            return factor !== '' && factor !== undefined && factor !== null && Number.isFinite(n) && n !== 0
          })
      )
      .map((c) => {
        const elementOverrides = mapCombinationOverridesForApi(c.elementOverrides, sections, materials)
        return {
          id: c.id,
          name: c.name || undefined,
          kind: c.kind || undefined,
          factors: Object.fromEntries(
            Object.entries(c.factors || {}).map(([caseId, factor]) => [caseId, Number(factor) || 0])
          ),
          ...(Object.keys(elementOverrides).length > 0 ? { elementOverrides } : {}),
        }
      })
  }

  function buildPayload() {
    return {
      nodes: mapNodesForApi(nodes),
      elements: mapElementsForApi(elements, sections, materials),
      supports: mapSupportsForApi(supports),
      loadCases: buildLoadCasesPayload(),
      combinations: buildCombinationsPayload(),
    }
  }

  // Combinations for the moving-loads endpoint use the `terms[]` form (see HANDOFF.md): each static
  // load-case factor becomes a { loadCase, factor } term and each train factor a { train, factor }
  // term. Only combinations that reference at least one train are sent — the backend resolves them
  // per train position and returns them under that train's movingResponse.
  function buildMovingCombinationsPayload() {
    return combinations
      .map((c) => {
        const terms = []
        Object.entries(c.factors || {}).forEach(([caseId, factor]) => {
          const f = Number(factor)
          if (f) terms.push({ loadCase: caseId, factor: f })
        })
        Object.entries(c.trainFactors || {}).forEach(([trainId, factor]) => {
          const f = Number(factor)
          if (f) terms.push({ train: trainId, factor: f })
        })
        return { id: c.id, name: c.name || undefined, kind: c.kind || undefined, terms }
      })
      .filter((c) => c.terms.some((t) => t.train !== undefined))
  }

  function buildMovingLoadsPayload() {
    return {
      nodes: mapNodesForApi(nodes),
      elements: mapElementsForApi(elements, sections, materials),
      supports: mapSupportsForApi(supports),
      path: mapPathForApi(movingLoadsPath),
      trains: mapTrainsForApi(trains),
      loadCases: buildLoadCasesPayload(),
      combinations: buildMovingCombinationsPayload(),
      responses: MOVING_RESPONSES,
    }
  }

  async function handleCalculate() {
    setIsLoading(true)

    try {
      const payload = buildPayload()
      setData(payload)

      const response = await api.post('/structural/matrix-solver', payload)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      // Moving loads piggyback on the same "Calcular" action whenever a train is configured, so
      // the user doesn't need a second button/flow to see N/V/M under the moving train.
      if (trains.length > 0 && movingLoadsPath.segments.length > 0) {
        const movingPayload = buildMovingLoadsPayload()
        setMovingLoadsData(movingPayload)

        const movingResponse = await api.post('/structural/moving-loads', movingPayload)
        setMovingLoadsResult(movingResponse.data)

        if (movingResponse.rateLimit) {
          updateFromHeaders({
            'x-ratelimit-limit': movingResponse.rateLimit.limit.toString(),
            'x-ratelimit-remaining': movingResponse.rateLimit.remaining.toString(),
            'x-ratelimit-reset': movingResponse.rateLimit.reset,
          })
        }
      } else {
        setMovingLoadsResult('')
        setMovingLoadsData({})
      }

      await fetchRateLimitStatus()

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error(error.response?.data?.message || 'Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
    }
  }

  // Dimensions the bottom/top reinforcement (As/As') for a single element via /concrete/bending,
  // using the section's own concrete geometry and the element's peak design moment (see
  // StructureCanvas's buildDesignAsPayload for how that payload is put together). Only called for
  // an element that's pure bending (near-zero axial force), matching what that endpoint models.
  async function handleDesignAs(elementKey, payload) {
    if (!canCalculate) {
      toast.error('Você atingiu o limite de cálculos diários.')
      return
    }
    try {
      const response = await api.post('/concrete/bending', payload)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }
      await fetchRateLimitStatus()

      setElements((prev) =>
        prev.map((el) =>
          el.key === elementKey ? { ...el, as: Number(response.data.as), asPrime: Number(response.data.asl) } : el
        )
      )
      toast.success('Armadura dimensionada com sucesso!')
    } catch (error) {
      console.error('Erro ao dimensionar armadura:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error(error.response?.data?.message || 'Erro ao dimensionar armadura. Tente novamente.')
    }
  }

  // Calculates the Branson equivalent inertia (Ieq) for a single element via
  // /concrete/branson-inertia (see StructureCanvas's buildEquivalentInertiaPayload for how that
  // payload is put together) and stores it as a new generic section — reusing the source section's
  // own area — rather than overwriting the element's section, so the user applies it deliberately
  // (e.g. via a per-combination section override for an ELS deflection check).
  async function handleCalculateEquivalentInertia(section, payload) {
    if (!canCalculate) {
      toast.error('Você atingiu o limite de cálculos diários.')
      return
    }
    try {
      const response = await api.post('/concrete/branson-inertia', payload)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }
      await fetchRateLimitStatus()

      const { A } = computeSectionProperties(section)
      const newSection = {
        ...createSection(sections, section.materialId),
        shape: 'generic',
        name: `Ieq — ${section.name || section.id}`,
        customA: A,
        customI: response.data.ieq / 1e8,
      }
      setSections((prev) => [...prev, newSection])
      toast.success(
        `Seção "${newSection.id}" criada com I_eq = ${response.data.ieq.toFixed(1)} cm⁴ (mesma área da seção "${section.id}").`
      )
    } catch (error) {
      console.error('Erro ao calcular inércia equivalente:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error(error.response?.data?.message || 'Erro ao calcular inércia equivalente. Tente novamente.')
    }
  }

  // Any edit to the model invalidates the computed results, so clear them (the canvas resets to
  // "Sem diagrama") and let the user recalculate manually. Loading a saved file is exempt: it
  // restores its own saved results, so we mark that model signature as already seen.
  const lastModelSignatureRef = useRef(null)
  const skipNextClearRef = useRef(false)

  useEffect(() => {
    const signature = JSON.stringify([
      nodes,
      elements,
      supports,
      nodalLoads,
      elementLoads,
      materials,
      sections,
      loadCases,
      combinations,
    ])

    if (signature === lastModelSignatureRef.current) return
    const isInitial = lastModelSignatureRef.current === null
    lastModelSignatureRef.current = signature
    if (isInitial) return

    if (skipNextClearRef.current) {
      skipNextClearRef.current = false
      return
    }

    setResult('')
    setData({})
  }, [nodes, elements, supports, nodalLoads, elementLoads, materials, sections, loadCases, combinations])

  // Same idea as above, but scoped to the moving-loads config (path/trains) plus the shared
  // nodes/elements/supports, since it has its own independent result.
  const lastMovingLoadsSignatureRef = useRef(null)
  const skipNextMovingLoadsClearRef = useRef(false)

  useEffect(() => {
    const signature = JSON.stringify([
      nodes,
      elements,
      supports,
      movingLoadsPath,
      trains,
      loadCases,
      nodalLoads,
      elementLoads,
      combinations,
    ])

    if (signature === lastMovingLoadsSignatureRef.current) return
    const isInitial = lastMovingLoadsSignatureRef.current === null
    lastMovingLoadsSignatureRef.current = signature
    if (isInitial) return

    if (skipNextMovingLoadsClearRef.current) {
      skipNextMovingLoadsClearRef.current = false
      return
    }

    setMovingLoadsResult('')
    setMovingLoadsData({})
  }, [nodes, elements, supports, movingLoadsPath, trains, loadCases, nodalLoads, elementLoads, combinations])

  function handleSave() {
    const stateData = {
      nodes,
      elements,
      supports,
      nodalLoads,
      elementLoads,
      materials,
      sections,
      loadCases,
      combinations,
      result,
      data,
      movingLoadsPath,
      trains,
      movingLoadsResult,
      movingLoadsData,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'Concrete Pórtico 2D.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)
        // Keep the results saved in the file instead of letting the model-change effect wipe them.
        skipNextClearRef.current = true
        skipNextMovingLoadsClearRef.current = true
        setNodes(jsonData.nodes || DEFAULT_NODES)
        setElements(jsonData.elements || DEFAULT_ELEMENTS)
        setSupports(jsonData.supports || DEFAULT_SUPPORTS)
        setNodalLoads(jsonData.nodalLoads || DEFAULT_NODAL_LOADS)
        setElementLoads(jsonData.elementLoads || DEFAULT_ELEMENT_LOADS)
        setMaterials(jsonData.materials || DEFAULT_MATERIALS)
        setSections(jsonData.sections || DEFAULT_SECTIONS)
        setLoadCases(jsonData.loadCases || DEFAULT_LOAD_CASES)
        setCombinations(jsonData.combinations || DEFAULT_COMBINATIONS)
        setResult(jsonData.result)
        setData(jsonData.data)
        setMovingLoadsPath(jsonData.movingLoadsPath || DEFAULT_PATH)
        setTrains(jsonData.trains || DEFAULT_TRAINS)
        setMovingLoadsResult(jsonData.movingLoadsResult || '')
        setMovingLoadsData(jsonData.movingLoadsData || {})
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Pórtico 2D</span>
          </div>
          <h1>🧮 Análise Matricial de Pórtico Plano 2D</h1>
          <p className="subtitle">
            Resolva estruturas planas por análise matricial elástica linear com elemento de pórtico 2D: deslocamentos
            nodais, reações de apoio e esforços internos
          </p>
          <div className="badges">
            <Badge>🧮 Análise Matricial</Badge>
            <Badge>📐 Elemento de Pórtico Plano</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Monte a estrutura informando nós, elementos de pórtico plano, apoios, cargas nodais e cargas nos elementos
              (distribuídas ou pontuais, em coordenadas locais ou globais). Cada nó possui 3 graus de liberdade:
              deslocamento horizontal (u), deslocamento vertical (v) e rotação (theta).
            </p>
            <p>
              O cálculo retorna os deslocamentos nodais, as reações de apoio e os esforços locais (normal, cortante e
              momento) em cada extremidade dos elementos.
            </p>
            <div className="norms">
              <span>Elemento de pórtico plano (3 GDL/nó)</span>
              <span>Análise elástica linear</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          <p style={{ marginBottom: '10px' }}>
            O solver não converte unidades. Mantenha um sistema consistente em todo o modelo (ex.: kN, m, kN/m², m²,
            m⁴).
          </p>

          <StructureCanvas
            nodes={nodes}
            elements={elements}
            supports={supports}
            nodalLoads={nodalLoads}
            elementLoads={elementLoads}
            materials={materials}
            sections={sections}
            loadCases={loadCases}
            combinations={combinations}
            setNodes={setNodes}
            setElements={setElements}
            setSupports={setSupports}
            setNodalLoads={setNodalLoads}
            setElementLoads={setElementLoads}
            setMaterials={setMaterials}
            setSections={setSections}
            setLoadCases={setLoadCases}
            setCombinations={setCombinations}
            disabled={isLoading}
            result={result}
            data={data}
            formErrors={combinedFormErrors}
            isFormValid={isFormValid}
            canCalculate={canCalculate}
            onCalculate={handleCalculate}
            onDesignAs={handleDesignAs}
            onCalculateEquivalentInertia={handleCalculateEquivalentInertia}
            onSave={handleSave}
            onFileUpload={handleFileUpload}
            movingLoadsPath={movingLoadsPath}
            trains={trains}
            setMovingLoadsPath={setMovingLoadsPath}
            setTrains={setTrains}
            movingLoadsResult={movingLoadsResult}
            movingLoadsFormErrors={movingLoadsFormErrors}
          />
        </Card>
      </Container>
    </PageContainer>
  )
}
